import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import invoiceService from '../../services/invoiceService';

const InvoiceManagement = () => {
    // State management
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Filter states
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [globalFilter, setGlobalFilter] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const [downloadingInvoices, setDownloadingInvoices] = useState({});
    const [error, setError] = useState(null);

    // Metrics state
    const [metrics, setMetrics] = useState({
        thisMonth: 0,
        thisQuarter: 0,
        halfYear: 0,
        thisYear: 0
    });

    const toast = useRef(null);

    // Initial mount — load companies + metrics (ONCE only)
    useEffect(() => {
        loadCompanies();
        loadMetrics();
    }, []);

    // Reload users when Company changes (also resets User selection)
    useEffect(() => {
        setSelectedUser(null);
        loadUsers();
    }, [selectedCompany]);

    // Reload invoices when pagination changes
    // Filter changes do NOT auto-trigger — user clicks Apply instead
    useEffect(() => {
        loadInvoices();
    }, [first, rows]);

    const loadCompanies = async () => {
        setLoadingCompanies(true);
        try {
            const response = await invoiceService.getCompaniesForFilter();
            if (response.success) {
                setCompanies(response.items || []);
            }
        } catch (error) {
            console.error('Error loading companies:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load companies',
                life: 5000
            });
        } finally {
            setLoadingCompanies(false);
        }
    };

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const params = {
                limit: 500,
                order: 'name_asc'
            };
            if (selectedCompany) {
                params.company_id = selectedCompany;
            }
            const response = await invoiceService.getUsersForFilter(params);
            if (response.success) {
                setUsers(response.items || []);
            }
        } catch (error) {
            console.error('Error loading users:', error?.message || 'Unknown error');
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadMetrics = async () => {
        try {
            const response = await invoiceService.getInvoiceMetrics();

            if (response.success) {
                setMetrics({
                    thisMonth: response.data.this_month || 0,
                    thisQuarter: response.data.this_quarter || 0,
                    halfYear: response.data.half_year || 0,
                    thisYear: response.data.this_year || 0
                });
            }
        } catch (error) {
            console.error('Error loading metrics:', error?.message || 'Unknown error');
        }
    };

    const loadInvoices = async () => {
        setLoading(true);
        setError(null);

        try {
            const page = Math.floor(first / rows) + 1;

            const formatDate = (date) => {
                if (!date) return undefined;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const params = {
                page: page,
                limit: rows,
                sort_field: 'invoice_date',
                sort_order: 'desc',
                company_ID: selectedCompany || undefined,
                user_id: selectedUser || undefined,
                search: globalFilter || undefined,
                date_from: formatDate(dateFrom),
                date_to: formatDate(dateTo)
            };

            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await invoiceService.getAdminInvoices(params);

            if (response.data) {
                setInvoices(response.data);
                setTotalRecords(response.total || 0);
            } else {
                setInvoices([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error loading invoices:', error?.message || 'Unknown error');
            setError('Failed to load invoices. Please try again.');
            setInvoices([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = () => {
        setFirst(0);
        loadInvoices();
    };

    const clearFilters = () => {
        setGlobalFilter('');
        setDateFrom(null);
        setDateTo(null);
        setSelectedCompany(null);
        setSelectedUser(null);
        setFirst(0);
        setTimeout(() => loadInvoices(), 0);
    };

    const handleDownloadInvoice = async (invoice) => {
        setDownloadingInvoices(prev => ({ ...prev, [invoice.id]: true }));

        try {
            await invoiceService.adminDownloadInvoice(invoice.id, invoice.invoice_number);
        } catch (error) {
            console.error('Download error:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Download failed',
                detail: error?.message || 'Could not download invoice',
                life: 5000
            });
        } finally {
            setDownloadingInvoices(prev => ({ ...prev, [invoice.id]: false }));
        }
    };

    // KPI card — uniform PrimeReact Card (2 per row on mobile, 4 per row on desktop)
    const KpiCard = ({ icon, label, value, description }) => (
        <div className="col-6 lg:col-3">
            <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                <div className="flex justify-content-between align-items-start">
                    <div className="flex flex-column gap-1">
                        <span className="text-600 text-sm font-medium">{label}</span>
                        <span className="text-900 text-xl font-semibold">{value}</span>
                    </div>
                    <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                        <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                    </div>
                </div>
                <span className="text-500 text-xs block mt-2">{description}</span>
            </Card>
        </div>
    );

    // Column templates
    const invoiceNumberTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-semibold text-primary">{rowData.invoice_number}</span>
            <small className="text-600">{invoiceService.formatDate(rowData.invoice_date)}</small>
        </div>
    );

    const companyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            <i className="pi pi-building text-primary" aria-hidden="true"></i>
            <div className="flex flex-column">
                {rowData.user?.company ? (
                    <>
                        <span className="font-medium">{rowData.user.company.name}</span>
                        <small className="text-500">{rowData.user.name}</small>
                    </>
                ) : (
                    <>
                        <span className="font-medium">{rowData.user.name}</span>
                        <small className="text-500">No Company</small>
                    </>
                )}
            </div>
        </div>
    );

    const userTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-medium">{rowData.user.name}</span>
            <small className="text-600">{rowData.user.email}</small>
        </div>
    );

    const amountTemplate = (rowData) => (
        <div className="text-right">
            <div className="font-semibold text-900">
                {invoiceService.formatCurrency(rowData.total_amount)}
            </div>
            <small className="text-600">
                {rowData.gst_type === 'intra_state' ? 'CGST+SGST' : 'IGST'}
            </small>
        </div>
    );

    const statusTemplate = (rowData) => (
        <Tag
            value={rowData.status?.toUpperCase()}
            severity={invoiceService.getStatusSeverity(rowData.status)}
        />
    );

    const actionTemplate = (rowData) => (
        <div className="flex flex-wrap justify-content-center gap-1">
            <Button
                icon="pi pi-download"
                severity="info"
                text
                size="small"
                tooltip="Download Invoice"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Download invoice ${rowData.invoice_number}`}
                onClick={() => handleDownloadInvoice(rowData)}
                loading={downloadingInvoices[rowData.id] || false}
                disabled={downloadingInvoices[rowData.id] || false}
            />
        </div>
    );

    // Dropdown item template for User (show name + email)
    const userItemTemplate = (option) => (
        <div className="flex flex-column">
            <span className="font-medium">{option.name}</span>
            <small className="text-500">{option.email}</small>
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Invoice Management</h1>
                        <p className="text-600 text-sm m-0">
                            Manage all tenant invoices and download receipts
                        </p>
                    </div>

                    {/* KPI cards — 2 per row on mobile (mandatory), 4 per row on desktop */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-calendar"
                            label="This Month"
                            value={metrics.thisMonth}
                            description="Invoices this month"
                        />
                        <KpiCard
                            icon="pi-chart-bar"
                            label="This Quarter"
                            value={metrics.thisQuarter}
                            description="Current quarter invoices"
                        />
                        <KpiCard
                            icon="pi-chart-line"
                            label="Half Year"
                            value={metrics.halfYear}
                            description="Last 6 months"
                        />
                        <KpiCard
                            icon="pi-file"
                            label="This Year"
                            value={metrics.thisYear}
                            description="Total this year"
                        />
                    </div>

                    {error && (
                        <Message
                            severity="error"
                            text={error}
                            className="w-full mb-3"
                        />
                    )}

                    <Card>
                        {/* Filter bar — tinted, 5 equal-width filters; action block below in width of 1 filter unit */}
                        <div className="surface-ground p-3 border-round mb-3">
                            {/* Row 1: max 5 filters.
                                Mobile:
                                  Row 1: Company (col-6), User (col-6)
                                  Row 2: From (col-6), To (col-6)          ← dates side by side
                                  Row 3: Invoice # (col-12)                ← full width
                                Desktop: 5 equal-flex columns in one row */}
                            <div className="grid">
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedCompany}
                                        options={companies}
                                        onChange={(e) => setSelectedCompany(e.value)}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name"
                                        placeholder="All Companies"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        loading={loadingCompanies}
                                        emptyMessage="No companies found"
                                        aria-label="Filter by company"
                                    />
                                </div>

                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedUser}
                                        options={users}
                                        onChange={(e) => setSelectedUser(e.value)}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name,email"
                                        placeholder="All Users"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        loading={loadingUsers}
                                        emptyMessage="No users found"
                                        itemTemplate={userItemTemplate}
                                        aria-label="Filter by user"
                                    />
                                </div>

                                <div className="col-6 md:col">
                                    <Calendar
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="From Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        maxDate={dateTo || new Date()}
                                        aria-label="Filter from date"
                                    />
                                </div>

                                <div className="col-6 md:col">
                                    <Calendar
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="To Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        minDate={dateFrom}
                                        maxDate={new Date()}
                                        aria-label="Filter to date"
                                    />
                                </div>

                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={globalFilter}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
                                            placeholder="Invoice #"
                                            className="w-full p-inputtext-sm"
                                            aria-label="Search by invoice number"
                                        />
                                    </IconField>
                                </div>
                            </div>

                            {/* Row 2: action block (Clear + Apply together as single unit).
                                Mobile: full-width below filters.
                                Desktop: width = 1 filter unit, right-aligned via 4 spacers */}
                            <div className="grid mt-2">
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        onClick={clearFilters}
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        className="flex-1"
                                    />
                                    <Button
                                        label="Apply"
                                        icon="pi pi-check"
                                        onClick={handleFilterChange}
                                        size="small"
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={invoices}
                            lazy
                            loading={loading}
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={(e) => {
                                setFirst(e.first);
                                setRows(e.rows);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-file pi-2x text-400 mb-3" />
                                    <span className="text-600">No invoices found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="invoice_number"
                                header="Invoice"
                                body={invoiceNumberTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="user.company.name"
                                header="Company"
                                body={companyTemplate}
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="user.name"
                                header="User"
                                body={userTemplate}
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="total_amount"
                                header="Amount"
                                body={amountTemplate}
                                sortable
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={statusTemplate}
                                sortable
                                style={{ minWidth: '110px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionTemplate}
                                style={{ minWidth: '100px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default InvoiceManagement;
